import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AnnotationConstraints, ConnectorModel, Diagram, DiagramComponent, DiagramModule, DiagramRegions, IExportOptions, NodeModel, PageSettingsModel, PrintAndExport, RulerSettingsModel, ScrollSettingsModel, SelectorConstraints, SelectorModel } from '@syncfusion/ej2-angular-diagrams';
import { ANGULAR_MODULES, OperationsComponent, SYNCFUSION_MODULES, TopBarComponent } from 'src/app/Shared/shared-imports';
Diagram.Inject(PrintAndExport);
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { GeneralService } from 'src/app/services/general.service';
import { EActionTypes, ECheckLayout, ELovTypes, EPrintOrientation, EPrintPart, EToaster, EUserSecurityFunctionID } from 'src/app/Shared/enum';
import { CheckWritingService } from 'src/app/services/checkWriting.service';
import { ICheckLayoutSetUp, ICheckSetUpDetails } from 'src/app/Shared/Interfaces/ICheckWriting';
import { ICommon, IUserPermissionHandler } from 'src/app/Shared/model/CommonModel';
import { SharedHTMLSettingsDataService } from 'src/app/Shared/sharedHTMLSettingsData.service';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { UserPermissionHandlerDirective } from 'src/app/Shared/userPermissionHandler.directive';
import { LoaderComponent } from 'src/app/Shared/loader/loader.component';
import printJS from 'print-js';
import { Router } from '@angular/router';
import { NodeConstraints } from '@syncfusion/ej2-angular-diagrams';

@Component({
  selector: 'app-ops-check-layout',
  imports: [SYNCFUSION_MODULES, ANGULAR_MODULES, OperationsComponent, TopBarComponent, DiagramModule, UserPermissionHandlerDirective, LoaderComponent],
  providers: [CheckWritingService],
  standalone: true,
  templateUrl: './ops-check-layout.html',
  styleUrl: './ops-check-layout.scss'
})
export class OpsCheckLayout implements OnInit, OnDestroy {

  @ViewChild('diagramPrint') public diagramPrint: DiagramComponent;
  @ViewChild('diagramDynamic') public diagramDynamic: DiagramComponent;
  public selectedItems?: SelectorModel;
  public loaderIcon: Promise<boolean>;
  private destroy$ = new Subject<void>();
  rulerSettings: RulerSettingsModel = { showRulers: true };
  public pageSettings: PageSettingsModel = { width: 1100, height: 400 };
  public scrollSettings: ScrollSettingsModel = {
    scrollLimit: 'Diagram',
    canAutoScroll: false,
    maxZoom: 40,
    minZoom: 0.3,
  };
  checkLayoutForm: FormGroup;
  viewPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.CheckLayout, Action: EActionTypes.VIEW };
  editPermissionHandlerData: IUserPermissionHandler = { FunctionId: EUserSecurityFunctionID.CheckLayout, Action: EActionTypes.EDIT };
  isCollapsed = true;
  fontNameList = [];
  fontStyleList = [];
  fontSizeList = [];
  checkSetupDetails: ICheckSetUpDetails;
  printOrientation: ICommon[] = [];
  checkPrintPositions = [];
  fieldLineSize: ICommon[] = [];
  checkFieldControlsList = [];
  dataCheckLayoutSetUpList: ICheckLayoutSetUp[] = [];
  public printSignatureOnChecks: boolean = false;
  public checkSignatureUrl: string = '';

  constructor(
    private formBuilder: FormBuilder,
    private generalService: GeneralService,
    private checkWritingService: CheckWritingService,
    private sharedHTMLSettings: SharedHTMLSettingsDataService,
    private router: Router,
    private toastr: ToastrService) { }

  ngOnInit(): void {

    let diagramWidth: ECheckLayout = ECheckLayout.EJSDiagramWidth;
    let diagramHeight: ECheckLayout = ECheckLayout.DiagramHeight;
    let width: number = diagramWidth;
    let height: number = diagramHeight;
    this.pageSettings = { width: width, height: height };
    this.initializeCheckLayoutForm();
    this.checkFieldControlsList = JSON.parse(JSON.stringify(this.checkWritingService.checkFieldControlsList));
    this.printOrientation = this.checkWritingService.printOrientation;
    this.checkPrintPositions = JSON.parse(JSON.stringify(this.checkWritingService.checkPrintPositions));
    this.checkLayoutDetailsJoinCall();
    this.fieldLineSize = JSON.parse(JSON.stringify(this.checkWritingService.fieldLineSize));
    this.getFontSize();
    this.selectedItems = { constraints: SelectorConstraints.All & ~SelectorConstraints.ToolTip };
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkLayoutDetailsJoinCall() {
    this.loaderIcon = Promise.resolve(true);
    forkJoin({
      checkSetUpDetails: this.checkWritingService.getCheckSetUpDetails(),
      checkLayoutSetUpList: this.checkWritingService.getCheckLayoutSetUpList(),
      fontName: this.generalService.getListOfValuesByType(ELovTypes['Font Name']),
      fontStyle: this.generalService.getListOfValuesByType(ELovTypes['Font Style']),
    }).subscribe({
      next: ({ checkSetUpDetails, checkLayoutSetUpList, fontName, fontStyle }) => {
        this.fontNameList = fontName;
        this.fontStyleList = fontStyle;
        this.getCheckSetUpDetails(checkSetUpDetails);
        this.dataCheckLayoutSetUpList = checkLayoutSetUpList;
        this.getCheckLayoutDetails(checkLayoutSetUpList);
      },
      error: () => { }
    });
  }

  getCheckSetUpDetails(data) {
    this.checkSetupDetails = data;
    if (data) {
      this.printSignatureOnChecks = data.PrintSignatureOnChecks ?? false;
      this.checkSignatureUrl = data.CheckSignatureUrl ?? '';
      this.checkLayoutForm.get('PrintOrientation').patchValue(data.CheckOrientation);
      switch (data.CheckOrientation) {
        case EPrintOrientation['1PerPage']: this.checkPrintPositions.map(p => {
          p.Visible = (p.Id > EPrintOrientation['1PerPage']) ? false : true;
        });
          break;
        case EPrintOrientation['2PerPage']: this.checkPrintPositions.map(p => {
          p.Visible = (p.Id == EPrintOrientation['2PerPage']) ? false : true;
        });
          break;
        case EPrintOrientation['3PerPage']: this.checkPrintPositions.map(p => {
          p.Visible = true;
        });
          break;
      }
    }
  }

  getCheckLayoutDetails(controlsList: ICheckLayoutSetUp[]) {
    if (controlsList && controlsList.length > 0) {
      this.mapControlsToDiagram(controlsList, false);
    } else {
      this.mapControlsToDiagram(JSON.parse(JSON.stringify(this.checkFieldControlsList)), true);
    }
  }

  mapCheckSetUpData(controlName: string, respectiveDefaultValue: string = null, lineLength: number = null, fontSize = null) {
    let content: string;
    switch (controlName) {
      case 'txtCheckDate':
        const today = new Date();
        const month = (today.getMonth() + 1).toString().padStart(2, '0');
        const day = today.getDate().toString().padStart(2, '0');
        const year = today.getFullYear();
        content = `${month}/${day}/${year}`;
        break;
      case 'txtCheckDateText':
        content = 'Date';
        break;
      case 'txtCompanyName':
        content = `${this.checkSetupDetails.MarketName}
      ${this.checkSetupDetails.Address}
      ${this.checkSetupDetails.City} ${this.checkSetupDetails.State}
      ${this.checkSetupDetails.ZipCode}`;
        break;
      case 'txtBankName':
        content = `${this.checkSetupDetails.BankName}
      ${this.checkSetupDetails.BankAddress}
      ${this.checkSetupDetails.BankCity} ${this.checkSetupDetails.BankState}
      ${this.checkSetupDetails.BankZipCode}`;
        break;
      case 'txtBankTransitCode': content = this.checkSetupDetails.TransitCode;
        break;
      case 'txtCheckNumber': content = this.checkSetupDetails.CurrentCheckNumber?.toString();
        break;
      case 'lnAmountLine':
      case 'lnMemoLine':
      case 'lnPayto':
        content = '';
        break;
      case 'txtMICR':
        if (respectiveDefaultValue && respectiveDefaultValue != '') {
          content = respectiveDefaultValue;
        } else {
          const checkNumber = this.checkSetupDetails.CurrentCheckNumber
            ? this.checkSetupDetails.CurrentCheckNumber.toString().padStart(6, '0')
            : '000000';
          const routingNumber = this.checkSetupDetails.RoutingNo ?? '';
          const accountNumber = this.checkSetupDetails.AccountNo ?? '';
          content = `C${checkNumber}C A${routingNumber}A ${accountNumber} C`;
        }
        break;
    }
    return content;
  }

  modelControlsToNode(control: ICheckLayoutSetUp, restoreToDefault: boolean, checkPosition = ''): NodeModel {
    if (control.ControlName === 'imgSignature') {
      const controlConfig = this.checkFieldControlsList.find(co => co.ControlName === control.ControlName);
      let nodeModel: NodeModel = JSON.parse(JSON.stringify(this.sharedHTMLSettings.nodeDynamic));
      nodeModel.id = `${control.ControlName}${restoreToDefault ? checkPosition : control.CheckPrintPart}`;
      nodeModel.offsetX = restoreToDefault ? control.DefaultXPosition : control.XPosition;
      nodeModel.offsetY = restoreToDefault ? control.DefaultYPosition : control.YPosition;
      nodeModel.width = controlConfig?.Width ?? 180;
      nodeModel.height = controlConfig?.Height ?? 45;
      nodeModel.constraints = NodeConstraints.Default & ~NodeConstraints.Rotate;
      nodeModel.visible = control.IsVisible && this.printSignatureOnChecks && !!this.checkSignatureUrl;
      nodeModel.shape = { type: 'Image', source: this.checkSignatureUrl };
      nodeModel.annotations = [];
      nodeModel.style = { fill: 'transparent', strokeColor: 'transparent' };
      nodeModel.addInfo = {
        CheckLayoutSetupId: this.controlIdMapGenerate(control, restoreToDefault, checkPosition),
        ControlName: control.ControlName,
        ControlCaption: control.ControlCaption,
        IsVisible: control.IsVisible,
        XPosition: control.XPosition,
        YPosition: control.YPosition,
        DefaultXPosition: control.DefaultXPosition,
        DefaultYPosition: control.DefaultYPosition,
        DefaultValue: control.DefaultValue,
        DefaultLineLength: control.DefaultLineLength,
        FontNameId: null,
        FontStyleId: null,
        FontSize: null,
        IsLine: false,
        LineLength: control.LineLength,
        LineSize: null,
        CheckPrintPart: restoreToDefault ? checkPosition : control.CheckPrintPart
      };
      return nodeModel;
    }

    let nodeModel: NodeModel = JSON.parse(JSON.stringify(this.sharedHTMLSettings.nodeDynamic));
    nodeModel.id = `${control.ControlName}${restoreToDefault ? checkPosition : control.CheckPrintPart}`;
    const amountInWordsObject = this.checkFieldControlsList.find(co => co.ControlName == 'txtAmountinWords');
    const content = this.mapCheckSetUpData(control.ControlName);
    nodeModel.annotations = [{
      content: (control.ControlName == 'txtAmountinWords') ? (this.checkWritingService.getAmountInLineWithSymbol(content ?? control.DefaultValue, amountInWordsObject.DefaultLineLength, control.FontSize))
        : (content ?? control.DefaultValue),
      style: {
        fontFamily: control.ControlName == "txtMICR" ? 'MICR' : this.fontNameList.find(f => f.Id == control.FontNameId)?.Name,
        bold: (control.FontStyleId && this.fontStyleList.find(f => f.Id == control.FontStyleId).Name == ECheckLayout.Bold) ? true : false,
        italic: (control.FontStyleId && this.fontStyleList.find(f => f.Id == control.FontStyleId).Name == ECheckLayout.Italic) ? true : false,
        fontSize: control.FontSize ?? ECheckLayout.CheckDefaultFontSize,
        textAlign: 'Left',
        textWrapping: 'NoWrap'
      },
      horizontalAlignment: 'Stretch'
    }];
    nodeModel.offsetX = restoreToDefault ? control.DefaultXPosition : control.XPosition;
    nodeModel.offsetY = restoreToDefault ? control.DefaultYPosition : control.YPosition;
    nodeModel.width = this.checkFieldControlsList.find(co => co.ControlName == control.ControlName).Width;
    nodeModel.visible = control.IsVisible;
    const id = this.controlIdMapGenerate(control, restoreToDefault, checkPosition);
    nodeModel.addInfo = {
      CheckLayoutSetupId: id,
      ControlName: control.ControlName,
      ControlCaption: control.ControlCaption,
      IsVisible: control.IsVisible,
      XPosition: control.XPosition,
      YPosition: control.YPosition,
      DefaultXPosition: control.DefaultXPosition,
      DefaultYPosition: control.DefaultYPosition,
      DefaultValue: control.DefaultValue,
      DefaultLineLength: null,
      FontNameId: control.FontNameId,
      FontStyleId: control.FontStyleId,
      FontSize: control.FontSize ?? ECheckLayout.CheckDefaultFontSize,
      IsLine: false,
      LineLength: null,
      LineSize: null,
      CheckPrintPart: restoreToDefault ? checkPosition : control.CheckPrintPart,
    };
    return nodeModel;
  }

  controlIdMapGenerate(control, restoreToDefault, checkPosition) {
    let existsId = control.CheckLayoutSetupId ?? 0;
    if (restoreToDefault) {
      const layoutcontrolSavedInDB = this.dataCheckLayoutSetUpList.find(rec => rec.CheckLayoutSetupId && rec.ControlName == control.ControlName && checkPosition == rec.CheckPrintPart);
      if (layoutcontrolSavedInDB) {
        existsId = layoutcontrolSavedInDB.CheckLayoutSetupId;
      } else {
        existsId = 0;
      }
    }
    return existsId;
  }

  modelControlsToConnector(control: ICheckLayoutSetUp, restoreToDefault: boolean, checkPosition = ''): ConnectorModel {
    const connectorModel: ConnectorModel = this.sharedHTMLSettings.connectorDynamic;
    connectorModel.id = `${control.ControlName}${restoreToDefault ? checkPosition : control.CheckPrintPart}`;

    connectorModel.annotations = [{
      content: '',
      offset: 0,
      alignment: 'Before',
      displacement: { x: 0, y: 5 },
      constraints: AnnotationConstraints.ReadOnly,
      style: {
        fontFamily: this.fontNameList.find(f => f.Id == control.FontNameId)?.Name,
        bold: (control.FontStyleId && this.fontStyleList.find(f => f.Id == control.FontStyleId).Name == ECheckLayout.Bold) ? true : false,
        italic: (control.FontStyleId && this.fontStyleList.find(f => f.Id == control.FontStyleId).Name == ECheckLayout.Italic) ? true : false,
        fontSize: control.FontSize ?? ECheckLayout.CheckDefaultFontSize
      }
    }];
    if (restoreToDefault) {
      connectorModel.sourcePoint = { x: control.DefaultXPosition, y: control.DefaultYPosition };
      connectorModel.targetPoint = { x: control.DefaultXPosition + control.DefaultLineLength, y: (control.DefaultYPosition) };
    } else {
      connectorModel.sourcePoint = { x: control.XPosition, y: control.YPosition };
      connectorModel.targetPoint = { x: control.XPosition + control.LineLength, y: (control.YPosition) };
    }
    connectorModel.style = { strokeWidth: control.LineSize };
    connectorModel.visible = control.IsVisible;
    const id = this.controlIdMapGenerate(control, restoreToDefault, checkPosition);
    connectorModel.addInfo = {
      CheckLayoutSetupId: id,
      ControlName: control.ControlName,
      ControlCaption: control.ControlCaption,
      IsVisible: control.IsVisible,
      XPosition: control.XPosition,
      YPosition: control.YPosition,
      DefaultXPosition: control.DefaultXPosition,
      DefaultYPosition: control.DefaultYPosition,
      DefaultValue: control.DefaultValue,
      IsLine: true,
      LineSize: control.LineSize,
      LineLength: restoreToDefault ? control.DefaultLineLength : control.LineLength,
      DefaultLineLength: control.DefaultLineLength,
      FontNameId: control.FontNameId,
      FontStyleId: control.FontStyleId,
      FontSize: control.FontSize ?? ECheckLayout.CheckDefaultFontSize,
      CheckPrintPart: restoreToDefault ? checkPosition : control.CheckPrintPart,
    };
    return connectorModel;
  }

  mapControlsToDiagram(controlsList: ICheckLayoutSetUp[], restoreToDefault: boolean) {
    this.checkLayoutForm.get('CheckPositionInSheet').setValue(EPrintPart.Top);
    let objPrintPositionT = this.checkPrintPositions.find(pos => pos.Id == EPrintPart.Top);
    objPrintPositionT['Connectors'] = [];
    objPrintPositionT['Nodes'] = [];
    let objPrintPositionM = this.checkPrintPositions.find(pos => pos.Id == EPrintPart.Middle);
    objPrintPositionM['Connectors'] = [];
    objPrintPositionM['Nodes'] = [];
    let objPrintPositionB = this.checkPrintPositions.find(pos => pos.Id == EPrintPart.Bottom);
    objPrintPositionB['Connectors'] = [];
    objPrintPositionB['Nodes'] = [];
    (this.diagramDynamic as Diagram).clear();
    controlsList.forEach(control => {
      if (control.ControlName === 'imgSignature') {
        control.IsVisible = this.printSignatureOnChecks && !!this.checkSignatureUrl;
      }
    });
    switch (this.checkLayoutForm.get('PrintOrientation').value) {
      case EPrintOrientation['1PerPage']:
        controlsList.forEach(control => {
          if (control.CheckPrintPart == EPrintPart[EPrintPart.Top] || restoreToDefault) {
            if (control.IsLine) {
              const nodeOrconnector: (ConnectorModel) = this.modelControlsToConnector(JSON.parse(JSON.stringify(control)), restoreToDefault, EPrintPart[EPrintPart.Top]);
              objPrintPositionT['Connectors'].push(JSON.parse(JSON.stringify(nodeOrconnector)));
              (this.diagramDynamic as Diagram).add(nodeOrconnector);
            } else {
              const nodeOrconnector: (NodeModel) = this.modelControlsToNode(JSON.parse(JSON.stringify(control)), restoreToDefault, EPrintPart[EPrintPart.Top]);
              objPrintPositionT['Nodes'].push(JSON.parse(JSON.stringify(nodeOrconnector)));
              (this.diagramDynamic as Diagram).add(nodeOrconnector);
            }
          }
        });
        break;
      case EPrintOrientation['2PerPage']:
        controlsList.forEach(control => {
          if (control.CheckPrintPart == EPrintPart[EPrintPart.Top] || restoreToDefault) {

            if (control.IsLine) {
              const nodeOrconnector: (NodeModel | ConnectorModel) = this.modelControlsToConnector(control, restoreToDefault, EPrintPart[EPrintPart.Top]);
              objPrintPositionT['Connectors'].push(JSON.parse(JSON.stringify(nodeOrconnector)));
              (this.diagramDynamic as Diagram).add(nodeOrconnector);
            } else {
              const nodeOrconnector: (NodeModel | ConnectorModel) = this.modelControlsToNode(control, restoreToDefault, EPrintPart[EPrintPart.Top]);
              objPrintPositionT['Nodes'].push(JSON.parse(JSON.stringify(nodeOrconnector)));
              (this.diagramDynamic as Diagram).add(nodeOrconnector);
            }
          }
          if (control.CheckPrintPart == EPrintPart[EPrintPart.Bottom] || restoreToDefault) {

            if (control.IsLine) {
              const nodeOrconnector: (NodeModel | ConnectorModel) = this.modelControlsToConnector(JSON.parse(JSON.stringify(control)), restoreToDefault, EPrintPart[EPrintPart.Bottom]);
              objPrintPositionB['Connectors'].push(JSON.parse(JSON.stringify(nodeOrconnector)));
            } else {
              const nodeOrconnector: (NodeModel | ConnectorModel) = this.modelControlsToNode(JSON.parse(JSON.stringify(control)), restoreToDefault, EPrintPart[EPrintPart.Bottom]);
              objPrintPositionB['Nodes'].push(JSON.parse(JSON.stringify(nodeOrconnector)));
            }
          }
        });
        break;
      case EPrintOrientation['3PerPage']:
        controlsList.forEach(control => {
          if (control.CheckPrintPart == EPrintPart[EPrintPart.Top] || restoreToDefault) {
            if (control.IsLine) {
              const nodeOrconnector: (NodeModel | ConnectorModel) = this.modelControlsToConnector(JSON.parse(JSON.stringify(control)), restoreToDefault, EPrintPart[EPrintPart.Top]);
              objPrintPositionT['Connectors'].push(JSON.parse(JSON.stringify(nodeOrconnector)));
              (this.diagramDynamic as Diagram).add(nodeOrconnector);
            } else {
              const nodeOrconnector: (NodeModel | ConnectorModel) = this.modelControlsToNode(JSON.parse(JSON.stringify(control)), restoreToDefault, EPrintPart[EPrintPart.Top]);
              objPrintPositionT['Nodes'].push(JSON.parse(JSON.stringify(nodeOrconnector)));
              (this.diagramDynamic as Diagram).add(nodeOrconnector);
            }
          }
          if (control.CheckPrintPart == EPrintPart[EPrintPart.Middle] || restoreToDefault) {

            if (control.IsLine) {
              const nodeOrconnector: (NodeModel | ConnectorModel) = this.modelControlsToConnector(JSON.parse(JSON.stringify(control)), restoreToDefault, EPrintPart[EPrintPart.Middle]);
              objPrintPositionM['Connectors'].push(JSON.parse(JSON.stringify(nodeOrconnector)));
            } else {
              const nodeOrconnector: (NodeModel | ConnectorModel) = this.modelControlsToNode(JSON.parse(JSON.stringify(control)), restoreToDefault, EPrintPart[EPrintPart.Middle]);
              objPrintPositionM['Nodes'].push(JSON.parse(JSON.stringify(nodeOrconnector)));
            }
          }
          if (control.CheckPrintPart == EPrintPart[EPrintPart.Bottom] || restoreToDefault) {

            if (control.IsLine) {
              const nodeOrconnector: (NodeModel | ConnectorModel) = this.modelControlsToConnector(JSON.parse(JSON.stringify(control)), restoreToDefault, EPrintPart[EPrintPart.Bottom]);
              objPrintPositionB['Connectors'].push(JSON.parse(JSON.stringify(nodeOrconnector)));
            } else {
              const nodeOrconnector: (NodeModel | ConnectorModel) = this.modelControlsToNode(JSON.parse(JSON.stringify(control)), restoreToDefault, EPrintPart[EPrintPart.Bottom]);
              objPrintPositionB['Nodes'].push(JSON.parse(JSON.stringify(nodeOrconnector)));
            }
          }
        });
        break;
    }
    this.loaderIcon = Promise.resolve(false);
  }

  restoreToDefault() {
    this.loaderIcon = Promise.resolve(true);
    if (this.dataCheckLayoutSetUpList && this.dataCheckLayoutSetUpList.length > 0) {
      this.mapControlsToDiagram(this.checkFieldControlsList, true);
    } else {
      this.mapControlsToDiagram(this.checkFieldControlsList, true);
    }
  }

  handleToggle(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  getFontSize() {
    this.fontSizeList = this.sharedHTMLSettings.getFontSize();
  }

  private initializeCheckLayoutForm(): void {
    this.checkLayoutForm = this.formBuilder.group({
      CheckPositionInSheet: [EPrintPart.Top],
      CheckFieldId: ['null'],
      CheckFieldVisible: [false],
      FieldLocationXPosition: [''],
      FieldLocationYPosition: [''],
      FieldLocationLineSize: [''],
      FieldLocationLineLength: [''],
      FieldSelected: [''],
      FieldText: [''],
      ChangeFontSettings: [false],
      FontNameId: [null],
      FontStyleId: [null],
      FontSize: [ECheckLayout.CheckDefaultFontSize],
      PrintOrientation: [1]
    });
    this.checkLayoutForm.get('PrintOrientation').disable();
    this.checkLayoutForm.get('FieldSelected').disable();
    this.checkLayoutForm.get('FieldText').disable();
    this.checkLayoutForm.get('FontSize').setValue(ECheckLayout.CheckDefaultFontSize);
    this.changeFontSettings(false);
    this.checkLayoutForm.get('CheckFieldVisible').disable();
    this.checkLayoutForm.get('FieldLocationXPosition').disable();
    this.checkLayoutForm.get('FieldLocationYPosition').disable();
    this.checkLayoutForm.get('FieldLocationLineSize').disable();
    this.checkLayoutForm.get('FieldLocationLineLength').disable();
    this.checkLayoutForm.get('ChangeFontSettings').disable();
  }

  changeFontSettings(event) {
    if (event) {
      this.checkLayoutForm.get('FontNameId').enable();
      this.checkLayoutForm.get('FontStyleId').enable();
      this.checkLayoutForm.get('FontSize').enable();
    } else {
      this.checkLayoutForm.get('FontNameId').disable();
      this.checkLayoutForm.get('FontStyleId').disable();
      this.checkLayoutForm.get('FontSize').disable();
    }
  }

  changeCheckFields(event, position, addcontrol: boolean, controlProperties = null) {
    this.checkLayoutForm.get('FieldText').setValue('');
    if (event != null && event != "null") {
      this.checkLayoutForm.get('CheckFieldVisible').enable();
      this.checkLayoutForm.get('FieldLocationXPosition').enable();
      this.checkLayoutForm.get('FieldLocationYPosition').enable();
      this.checkLayoutForm.get('FieldLocationLineLength').enable();
      let controlFieldObj = JSON.parse(JSON.stringify(this.checkFieldControlsList.find(c => c.ControlName == event)));
      const isSignature = this.isSignatureControl(controlFieldObj.ControlName);
      if (!isSignature) {
        this.checkLayoutForm.get('ChangeFontSettings').enable();
      }
      else {
        this.checkLayoutForm.get('ChangeFontSettings').disable();
      }
      if (!isSignature) {
        this.checkLayoutForm.get('FieldLocationLineSize').enable();
      }
      else {
        this.checkLayoutForm.get('FieldLocationLineSize').disable();
      }
      const checkPositionObject = this.checkPrintPositions.find(c => (c.Id == this.checkLayoutForm.get('CheckPositionInSheet').value));
      if (checkPositionObject) {
        const existingControlObject = controlFieldObj.IsLine ? checkPositionObject.Connectors.find(rec => rec.addInfo.ControlName == controlFieldObj.ControlName) : checkPositionObject.Nodes.find(rec => rec.addInfo.ControlName == controlFieldObj.ControlName);
        if (existingControlObject) {
          controlFieldObj = JSON.parse(JSON.stringify(existingControlObject.addInfo));
        }
      }

      this.checkLayoutForm.patchValue({
        FieldSelected: controlFieldObj.ControlCaption,
        FieldText: controlFieldObj.DefaultValue
      });
      if (addcontrol) {
        if (controlFieldObj.IsLine) {
          this.validateConnectorPresentAddControl(controlFieldObj);
        } else {
          this.validateNodePresentAddControl(controlFieldObj);
        }
      } else {
        this.setcontrolXYPsitions(position.x, position.y);
        if (controlFieldObj.IsLine) {
          this.setControlLinePositions(controlFieldObj.LineLength, controlFieldObj.LineSize);
        } else {
          this.setControlLinePositions('', null);
        }
        this.setControlFontSetting(controlFieldObj.FontNameId, controlFieldObj.FontStyleId, controlFieldObj.FontSize);
      }
    } else {
      this.checkLayoutForm.patchValue({
        FieldLocationXPosition: '',
        FieldLocationYPosition: '',
        FieldLocationLineSize: null,
        FieldLocationLineLength: '',
        ChangeFontSettings: false,
        CheckFieldVisible: false
      });
      this.checkLayoutForm.get('CheckFieldVisible').disable();
      this.checkLayoutForm.get('FieldLocationXPosition').disable();
      this.checkLayoutForm.get('FieldLocationYPosition').disable();
      this.checkLayoutForm.get('FieldLocationLineSize').disable();
      this.checkLayoutForm.get('FieldLocationLineLength').disable();
      this.checkLayoutForm.get('ChangeFontSettings').disable();
      this.checkLayoutForm.get('FieldSelected').setValue('');
    }
  }

  setControlVisible(isVisible: boolean) {
    this.checkLayoutForm.get('CheckFieldVisible').patchValue(isVisible);
  }

  setControlFontSetting(fontNameId: number, fontStyleId: number, fontSize: number): void {
    this.checkLayoutForm.patchValue({
      FontNameId: fontNameId,
      FontStyleId: fontStyleId,
      FontSize: fontSize
    });
  }

  setcontrolXYPsitions(x, y) {
    this.checkLayoutForm.patchValue({
      FieldLocationXPosition: x,
      FieldLocationYPosition: y
    });
  }

  setControlLinePositions(lineLength, lineSize) {
    this.checkLayoutForm.patchValue({
      FieldLocationLineLength: lineLength,
      FieldLocationLineSize: lineSize
    });
  }

  updateControlProperties(event, formControlName) {
    const modifiedValue = Number(event.target.value);
    const checkPositionObject = this.checkPrintPositions.find(c => (c.Id == this.checkLayoutForm.get('CheckPositionInSheet').value));
    if (checkPositionObject) {
      const uniqueId = `${this.checkLayoutForm.get('CheckFieldId').value}${checkPositionObject.Name}`;
      const defaultControlObj = this.checkFieldControlsList.find(c => c.ControlName == this.checkLayoutForm.get('CheckFieldId').value);
      if (defaultControlObj.IsLine) {
        let cObject = checkPositionObject['Connectors'].find(rec => rec.id == uniqueId);
        const objectDiagramControl = (this.diagramDynamic as any).connectors.find(n => n.id == uniqueId);
        if (cObject) {
          switch (formControlName) {
            case 'FieldLocationXPosition':
              if (!isNaN(modifiedValue)) {
                objectDiagramControl.offsetX = modifiedValue ?? defaultControlObj.DefaultXPosition;
                objectDiagramControl.addInfo.XPosition = modifiedValue ?? defaultControlObj.DefaultXPosition;
                cObject.sourcePoint.x = modifiedValue ?? defaultControlObj.DefaultXPosition;
                cObject.addInfo.XPosition = modifiedValue ?? defaultControlObj.DefaultXPosition;
              } else {
                this.checkLayoutForm.get('FieldLocationXPosition').patchValue(objectDiagramControl.offsetX);
              }
              break;
            case 'FieldLocationYPosition':
              if (!isNaN(modifiedValue)) {
                objectDiagramControl.offsetY = modifiedValue ?? defaultControlObj.DefaultYPosition;
                objectDiagramControl.addInfo.YPosition = modifiedValue ?? defaultControlObj.DefaultYPosition;
                cObject.sourcePoint.y = modifiedValue ?? defaultControlObj.DefaultYPosition;
                cObject.addInfo.YPosition = modifiedValue ?? defaultControlObj.DefaultYPosition;
              } else {
                this.checkLayoutForm.get('FieldLocationYPosition').patchValue(objectDiagramControl.offsetY);
              }
              break;
            case 'FieldLocationLineSize':
              if (!isNaN(modifiedValue)) {
                objectDiagramControl.style.strokeWidth = modifiedValue ?? defaultControlObj.LineSize;
                objectDiagramControl.addInfo.LineSize = modifiedValue ?? defaultControlObj.LineSize;
                cObject.style.strokeWidth = modifiedValue ?? defaultControlObj.LineSize;
                cObject.addInfo.LineSize = modifiedValue ?? defaultControlObj.LineSize;
              } else {
                this.checkLayoutForm.get('FieldLocationLineSize').patchValue(objectDiagramControl.addInfo.LineSize);
              }
              break;
            case 'FieldLocationLineLength':
              if (!isNaN(modifiedValue)) {
                objectDiagramControl.targetPoint = { x: modifiedValue, y: this.checkLayoutForm.get('FieldLocationYPosition').value };
                objectDiagramControl.addInfo.LineLength = modifiedValue ?? defaultControlObj.DefaultLineLength;
                cObject.targetPoint = { x: modifiedValue, y: this.checkLayoutForm.get('FieldLocationYPosition').value };
                cObject.addInfo.LineLength = modifiedValue ?? defaultControlObj.DefaultLineLength;

                objectDiagramControl.annotations[0].content = '';
                cObject.annotations[0].content = '';
              } else {
                this.checkLayoutForm.get('FieldLocationLineLength').patchValue(objectDiagramControl.addInfo.LineLength);
              }
              break;
            case 'FontNameId':
              if (!isNaN(modifiedValue)) {
                objectDiagramControl.annotations[0].style.fontFamily = this.fontNameList.find(f => f.Id == modifiedValue)?.Name;
                cObject.annotations[0].style.fontFamily = this.fontNameList.find(f => f.Id == modifiedValue)?.Name;
                objectDiagramControl.FontNameId = modifiedValue;
                cObject.FontNameId = modifiedValue;
              } else {
                this.checkLayoutForm.get('FontNameId').patchValue(objectDiagramControl.FontNameId);
              }
              break;
            case 'FontStyleId':
              if (!isNaN(modifiedValue)) {
                objectDiagramControl.annotations[0].style.italic = this.fontStyleList.find(f => f.Id == modifiedValue).Name == ECheckLayout.Italic ? true : false;
                objectDiagramControl.annotations[0].style.bold = this.fontStyleList.find(f => f.Id == modifiedValue).Name == ECheckLayout.Bold ? true : false;
                objectDiagramControl.FontStyleId = modifiedValue,
                  cObject.FontStyleId = modifiedValue,
                  cObject.annotations[0].style.italic = this.fontStyleList.find(f => f.Id == modifiedValue).Name == ECheckLayout.Italic ? true : false;
                cObject.annotations[0].style.bold = this.fontStyleList.find(f => f.Id == modifiedValue).Name == ECheckLayout.Bold ? true : false;
              } else {
                this.checkLayoutForm.get('FontStyleId').patchValue(objectDiagramControl.FontStyleId);
              }
              break;
            case 'FontSize':
              if (!isNaN(modifiedValue)) {
                objectDiagramControl.annotations[0].style.fontSize = modifiedValue;
                cObject.annotations[0].style.fontSize = modifiedValue;
                objectDiagramControl.addInfo.FontSize = modifiedValue;
                cObject.addInfo.FontSize = modifiedValue;
                objectDiagramControl.annotations[0].content = '';
                cObject.annotations[0].content = '';
              } else {
                this.checkLayoutForm.get('FontSize').patchValue(objectDiagramControl.FontSize);
              }
              break;
            case 'CheckFieldVisible':
              const checkedVisible = event.target.checked;
              objectDiagramControl.visible = checkedVisible;
              cObject.visible = checkedVisible;
              objectDiagramControl.addInfo.IsVisible = checkedVisible;
              cObject.addInfo.IsVisible = checkedVisible;
              break;
          }
        }
      } else {
        let nObject = checkPositionObject['Nodes'].find(rec => rec.id == uniqueId);
        const objectDiagramControl = (this.diagramDynamic as any).nodes.find(n => n.id == uniqueId);
        if (nObject) {
          switch (formControlName) {
            case 'FieldLocationXPosition':
              if (!isNaN(modifiedValue)) {
                objectDiagramControl.offsetX = modifiedValue ?? defaultControlObj.DefaultXPosition;
                objectDiagramControl.addInfo.XPosition = modifiedValue ?? defaultControlObj.DefaultXPosition;
                nObject.offsetX = modifiedValue ?? defaultControlObj.DefaultXPosition;
                nObject.addInfo.XPosition = modifiedValue ?? defaultControlObj.DefaultXPosition;
              } else {
                this.checkLayoutForm.get('FieldLocationXPosition').patchValue(objectDiagramControl.offsetX);
              }
              break;
            case 'FieldLocationYPosition':
              if (!isNaN(modifiedValue)) {
                objectDiagramControl.offsetY = modifiedValue ?? defaultControlObj.DefaultYPosition;
                objectDiagramControl.addInfo.YPosition = modifiedValue ?? defaultControlObj.DefaultYPosition;
                nObject.offsetY = modifiedValue ?? defaultControlObj.DefaultYPosition;
                nObject.addInfo.YPosition = modifiedValue ?? defaultControlObj.DefaultYPosition;
              } else {
                this.checkLayoutForm.get('FieldLocationYPosition').patchValue(objectDiagramControl.offsetY);
              }
              break;
            case 'FontNameId':
              if (!isNaN(modifiedValue)) {
                objectDiagramControl.annotations[0].style.fontFamily = this.fontNameList.find(f => f.Id == modifiedValue)?.Name;
                nObject.annotations[0].style.fontFamily = this.fontNameList.find(f => f.Id == modifiedValue)?.Name;
                objectDiagramControl.FontNameId = modifiedValue;
                nObject.FontNameId = modifiedValue;
              } else {
                this.checkLayoutForm.get('FontNameId').patchValue(objectDiagramControl.FontNameId);
              }
              break;
            case 'FontStyleId':
              if (!isNaN(modifiedValue)) {
                objectDiagramControl.annotations[0].style.italic = this.fontStyleList.find(f => f.Id == modifiedValue).Name == ECheckLayout.Italic ? true : false;
                objectDiagramControl.annotations[0].style.bold = this.fontStyleList.find(f => f.Id == modifiedValue).Name == ECheckLayout.Bold ? true : false;
                objectDiagramControl.FontStyleId = modifiedValue,
                  nObject.FontStyleId = modifiedValue,
                  nObject.annotations[0].style.italic = this.fontStyleList.find(f => f.Id == modifiedValue).Name == ECheckLayout.Italic ? true : false;
                nObject.annotations[0].style.bold = this.fontStyleList.find(f => f.Id == modifiedValue).Name == ECheckLayout.Bold ? true : false;
              } else {
                this.checkLayoutForm.get('FontStyleId').patchValue(objectDiagramControl.FontStyleId);
              }
              break;
            case 'FontSize':
              if (!isNaN(modifiedValue)) {
                objectDiagramControl.annotations[0].style.fontSize = modifiedValue;
                nObject.annotations[0].style.fontSize = modifiedValue;
                objectDiagramControl.addInfo.FontSize = modifiedValue;
                nObject.addInfo.FontSize = modifiedValue;
              } else {
                this.checkLayoutForm.get('FontSize').patchValue(objectDiagramControl.FontSize);
              }
              break;
            case 'CheckFieldVisible':
              const checkedVisible = event.target.checked;
              objectDiagramControl.visible = checkedVisible;
              nObject.visible = checkedVisible;
              objectDiagramControl.addInfo.IsVisible = checkedVisible;
              nObject.addInfo.IsVisible = checkedVisible;
              break;
          }
        }
      }
    }
  }

  validateConnectorPresentAddControl(controlFieldObj) {
    const checkPositionObject = this.checkPrintPositions.find(c => (c.Id == this.checkLayoutForm.get('CheckPositionInSheet').value));
    if (checkPositionObject) {
      const uniqueId = `${controlFieldObj.ControlName}${checkPositionObject.Name}`;
      switch (checkPositionObject.Id) {
        case EPrintPart.Top:
          if (this.diagramDynamic.connectors.findIndex(n => n.id == uniqueId) == -1) {
            const nodeOrconnector: ConnectorModel = this.modelControlsToConnector(controlFieldObj, true, checkPositionObject.Name);
            checkPositionObject['Connectors'].push(JSON.parse(JSON.stringify(nodeOrconnector)));
            (this.diagramDynamic as Diagram).add(nodeOrconnector);
            this.setControlVisible(controlFieldObj.IsVisible);
            this.setcontrolXYPsitions(nodeOrconnector.sourcePoint, nodeOrconnector.targetPoint);
            this.setControlLinePositions(controlFieldObj.LineLength, controlFieldObj.LineSize);
            this.setControlFontSetting(controlFieldObj.FontNameId, controlFieldObj.FontStyleId, controlFieldObj.FontSize);
          } else {
            const objectDiagramControl = (this.diagramDynamic as any).connectors.find(n => n.id == uniqueId);
            (this.diagramDynamic as any).select([objectDiagramControl]);
            this.setControlVisible(controlFieldObj.IsVisible);
            this.setcontrolXYPsitions(objectDiagramControl.properties.sourcePoint.x, objectDiagramControl.properties.sourcePoint.y);
            this.setControlLinePositions(Math.abs(objectDiagramControl.targetPoint.x - objectDiagramControl.sourcePoint.x), objectDiagramControl.style.strokeWidth);
            this.setControlFontSetting(controlFieldObj.FontNameId, controlFieldObj.FontStyleId, controlFieldObj.FontSize);
            let cObject = checkPositionObject['Connectors'].find(rec => rec.id == uniqueId);
            if (cObject) {
              cObject.sourcePoint = objectDiagramControl.properties.sourcePoint.properties;
              cObject.targetPoint = objectDiagramControl.properties.targetPoint.properties;
              cObject.addInfo.XPosition = objectDiagramControl.properties.sourcePoint.x;
              cObject.addInfo.YPosition = objectDiagramControl.properties.sourcePoint.y;
              cObject.addInfo.LineLength = Math.abs(objectDiagramControl.targetPoint.x - objectDiagramControl.sourcePoint.x);
            }
          };
          break;
        case EPrintPart.Middle:
          if (this.diagramDynamic.connectors.findIndex(n => n.id == uniqueId) == -1) {
            const nodeOrconnector: ConnectorModel = this.modelControlsToConnector(controlFieldObj, true, checkPositionObject.Name);
            checkPositionObject['Connectors'].push(JSON.parse(JSON.stringify(nodeOrconnector)));
            (this.diagramDynamic as Diagram).add(nodeOrconnector);
            this.setControlVisible(controlFieldObj.IsVisible);
            this.setcontrolXYPsitions(nodeOrconnector.sourcePoint, nodeOrconnector.targetPoint);
            this.setControlLinePositions(controlFieldObj.LineLength, controlFieldObj.LineSize);
            this.setControlFontSetting(controlFieldObj.FontNameId, controlFieldObj.FontStyleId, controlFieldObj.FontSize);
          } else {
            const objectDiagramControl = (this.diagramDynamic as any).connectors.find(n => n.id == uniqueId);
            (this.diagramDynamic as any).select([objectDiagramControl]);
            this.setControlVisible(controlFieldObj.IsVisible);
            this.setControlFontSetting(controlFieldObj.FontNameId, controlFieldObj.FontStyleId, controlFieldObj.FontSize);
            this.setcontrolXYPsitions(objectDiagramControl.properties.sourcePoint.x, objectDiagramControl.properties.sourcePoint.y);
            this.setControlLinePositions(Math.abs(objectDiagramControl.targetPoint.x - objectDiagramControl.sourcePoint.x), objectDiagramControl.style.strokeWidth);
            let cObject = checkPositionObject['Connectors'].find(rec => rec.id == uniqueId);
            if (cObject) {
              cObject.sourcePoint = objectDiagramControl.properties.sourcePoint.properties;
              cObject.targetPoint = objectDiagramControl.properties.targetPoint.properties;
              cObject.addInfo.XPosition = objectDiagramControl.properties.sourcePoint.x;
              cObject.addInfo.YPosition = objectDiagramControl.properties.sourcePoint.y;
              cObject.addInfo.LineLength = Math.abs(objectDiagramControl.targetPoint.x - objectDiagramControl.sourcePoint.x);
            }
          };
          break;
        case EPrintPart.Bottom:
          if (this.diagramDynamic.connectors.findIndex(n => n.id == uniqueId) == -1) {
            const nodeOrconnector: ConnectorModel = this.modelControlsToConnector(controlFieldObj, true, checkPositionObject.Name);
            checkPositionObject['Connectors'].push(JSON.parse(JSON.stringify(nodeOrconnector)));
            (this.diagramDynamic as Diagram).add(nodeOrconnector);
            this.setControlVisible(controlFieldObj.IsVisible);
            this.setcontrolXYPsitions(nodeOrconnector.sourcePoint, nodeOrconnector.targetPoint);
            this.setControlLinePositions(controlFieldObj.LineLength, controlFieldObj.LineSize);
            this.setControlFontSetting(controlFieldObj.FontNameId, controlFieldObj.FontStyleId, controlFieldObj.FontSize);
          } else {
            const objectDiagramControl = (this.diagramDynamic as any).connectors.find(n => n.id == uniqueId);
            (this.diagramDynamic as any).select([objectDiagramControl]);
            this.setControlVisible(controlFieldObj.IsVisible);
            this.setControlFontSetting(controlFieldObj.FontNameId, controlFieldObj.FontStyleId, controlFieldObj.FontSize);
            this.setcontrolXYPsitions(objectDiagramControl.properties.sourcePoint.x, objectDiagramControl.properties.sourcePoint.y);
            this.setControlLinePositions(Math.abs(objectDiagramControl.targetPoint.x - objectDiagramControl.sourcePoint.x), objectDiagramControl.style.strokeWidth);
            let cObject = checkPositionObject['Connectors'].find(rec => rec.id == uniqueId);
            if (cObject) {
              cObject.sourcePoint = objectDiagramControl.properties.sourcePoint.properties;
              cObject.targetPoint = objectDiagramControl.properties.targetPoint.properties;
              cObject.addInfo.XPosition = objectDiagramControl.properties.sourcePoint.x;
              cObject.addInfo.YPosition = objectDiagramControl.properties.sourcePoint.y;
              cObject.addInfo.LineLength = Math.abs(objectDiagramControl.targetPoint.x - objectDiagramControl.sourcePoint.x);
            }
          };
          break;
      }
    }
  }

  validateNodePresentAddControl(controlFieldObj) {
    const checkPositionObject = this.checkPrintPositions.find(c => (c.Id == this.checkLayoutForm.get('CheckPositionInSheet').value));
    if (checkPositionObject) {
      const uniqueId = `${controlFieldObj.ControlName}${checkPositionObject.Name}`;
      switch (checkPositionObject.Id) {
        case EPrintPart.Top:
          if (this.diagramDynamic.nodes.findIndex(n => n.id == uniqueId) == -1) {
            const node: NodeModel = this.modelControlsToNode(controlFieldObj, true, checkPositionObject.Name);
            checkPositionObject['Nodes'].push(JSON.parse(JSON.stringify(node)));
            (this.diagramDynamic as Diagram).add(node);
            this.setControlVisible(controlFieldObj.IsVisible);
            this.setcontrolXYPsitions(node.offsetX, node.offsetY);
            this.setControlFontSetting(controlFieldObj.FontNameId, controlFieldObj.FontStyleId, controlFieldObj.FontSize);
            this.setControlLinePositions(null, null);
          } else {
            const objectDiagramControl = (this.diagramDynamic as any).nodes.find(n => n.id == uniqueId);
            (this.diagramDynamic as any).select([objectDiagramControl]);
            this.setControlVisible(controlFieldObj.IsVisible);
            this.setControlFontSetting(controlFieldObj.FontNameId, controlFieldObj.FontStyleId, controlFieldObj.FontSize);
            this.setcontrolXYPsitions(objectDiagramControl.properties.offsetX, objectDiagramControl.properties.offsetY);
            this.setControlLinePositions(null, null);
            let nodeObject = checkPositionObject['Nodes'].find(rec => rec.id == uniqueId);
            if (nodeObject) {
              nodeObject.offsetX = objectDiagramControl.properties.offsetX;
              nodeObject.offsetY = objectDiagramControl.properties.offsetY;
              nodeObject.addInfo.XPosition = objectDiagramControl.properties.offsetX;
              nodeObject.addInfo.YPosition = objectDiagramControl.properties.offsetY;
            }
          }
          break;
        case EPrintPart.Middle:
          if (this.diagramDynamic.nodes.findIndex(n => n.id == uniqueId) == -1) {
            const node: NodeModel = this.modelControlsToNode(controlFieldObj, true, checkPositionObject.Name);
            checkPositionObject['Nodes'].push(JSON.parse(JSON.stringify(node)));
            (this.diagramDynamic as Diagram).add(node);
            this.setControlVisible(controlFieldObj.IsVisible);
            this.setcontrolXYPsitions(node.offsetX, node.offsetY);
            this.setControlFontSetting(controlFieldObj.FontNameId, controlFieldObj.FontStyleId, controlFieldObj.FontSize);
            this.setControlLinePositions(null, null);
          } else {
            const objectDiagramControl = (this.diagramDynamic as any).nodes.find(n => n.id == uniqueId);
            (this.diagramDynamic as any).select([objectDiagramControl]);
            this.setControlVisible(controlFieldObj.IsVisible);
            this.setcontrolXYPsitions(objectDiagramControl.properties.offsetX, objectDiagramControl.properties.offsetY);
            this.setControlFontSetting(controlFieldObj.FontNameId, controlFieldObj.FontStyleId, controlFieldObj.FontSize);
            this.setControlLinePositions(null, null);
            let nodeObject = checkPositionObject['Nodes'].find(rec => rec.id == uniqueId);
            if (nodeObject) {
              nodeObject.offsetX = objectDiagramControl.properties.offsetX;
              nodeObject.offsetY = objectDiagramControl.properties.offsetY;
              nodeObject.addInfo.XPosition = objectDiagramControl.properties.offsetX;
              nodeObject.addInfo.YPosition = objectDiagramControl.properties.offsetY;
            }
          }
          break;
        case EPrintPart.Bottom:
          if (this.diagramDynamic.nodes.findIndex(n => n.id == uniqueId) == -1) {
            const node: NodeModel = this.modelControlsToNode(controlFieldObj, true, checkPositionObject.Name);
            checkPositionObject['Nodes'].push(JSON.parse(JSON.stringify(node)));
            (this.diagramDynamic as Diagram).add(node);
            this.setControlVisible(controlFieldObj.IsVisible);
            this.setcontrolXYPsitions(node.offsetX, node.offsetY);
            this.setControlFontSetting(controlFieldObj.FontNameId, controlFieldObj.FontStyleId, controlFieldObj.FontSize);
            this.setControlLinePositions(null, null);
          } else {
            const objectDiagramControl = (this.diagramDynamic as any).nodes.find(n => n.id == uniqueId);
            (this.diagramDynamic as any).select([objectDiagramControl]);
            this.setControlVisible(controlFieldObj.IsVisible);
            this.setcontrolXYPsitions(objectDiagramControl.properties.offsetX, objectDiagramControl.properties.offsetY);
            this.setControlFontSetting(controlFieldObj.FontNameId, controlFieldObj.FontStyleId, controlFieldObj.FontSize);
            this.setControlLinePositions(null, null);
            let nodeObject = checkPositionObject['Nodes'].find(rec => rec.id == uniqueId);
            if (nodeObject) {
              nodeObject.offsetX = objectDiagramControl.properties.offsetX;
              nodeObject.offsetY = objectDiagramControl.properties.offsetY;
              nodeObject.addInfo.XPosition = objectDiagramControl.properties.offsetX;
              nodeObject.addInfo.YPosition = objectDiagramControl.properties.offsetY;
            }
          }
          break;
      }
    }
  }

  changeCheckPositionInSheet(value) {
    if (value != 0 && value != null) {
      let objPrintPosition = this.checkPrintPositions.find(pos => pos.Id == value);
      (this.diagramDynamic as Diagram).clear();
      if (objPrintPosition['Nodes'].length > 0) {
        objPrintPosition['Nodes'].forEach(node => {
          (this.diagramDynamic as Diagram).add(node as NodeModel);
        });
      }
      if (objPrintPosition['Connectors'].length > 0) {
        objPrintPosition['Connectors'].forEach(con => {
          (this.diagramDynamic as Diagram).add(con as ConnectorModel);
        });
      }
      (this.diagramDynamic as Diagram).refresh();
      this.checkLayoutForm.patchValue({
        FieldLocationXPosition: '',
        FieldLocationYPosition: '',
        FieldLocationLineSize: null,
        FieldLocationLineLength: '',
        ChangeFontSettings: false,
        CheckFieldVisible: false,
        FontNameId: null,
        FontStyleId: null,
        FontSize: null,
        CheckFieldId: null,
        FieldSelected: '',
        FieldText: ''
      });
    }
  }

  btnCancel() {
    this.router.navigate(['/dashboard']);
  }

  exportDiagram() {
    const exportOptions: IExportOptions = {}
    exportOptions.region = 'PageSettings' as DiagramRegions;
    this.diagramPrint.clear();
    let checkPrintPositions = JSON.parse(JSON.stringify(this.checkPrintPositions));
    checkPrintPositions.forEach(page => {
      if (page.Connectors.length > 0) {
        page.Connectors.forEach(conn => {
          let connectorObj = JSON.parse(JSON.stringify(conn));
          if (page.Id == EPrintPart.Top) {
            const x1 = Number(this.checkSetupDetails.X1);
            const y1 = Number(this.checkSetupDetails.Y1);
            if ((!isNaN(x1) && x1 > 0) || (!isNaN(y1) && y1 > 0)) {
              if (!isNaN(x1) && x1 > 0) {
                connectorObj.sourcePoint.x = connectorObj.sourcePoint.x + x1;
                connectorObj.targetPoint.x = connectorObj.targetPoint.x + x1;
              }
              if (!isNaN(y1) && y1 > 0) {
                connectorObj.sourcePoint.y = connectorObj.sourcePoint.y + y1;
                connectorObj.targetPoint.y = connectorObj.targetPoint.y + y1;
              }
            }
            (this.diagramPrint as Diagram).add(JSON.parse(JSON.stringify(connectorObj)));
          } else if (page.Id == EPrintPart.Middle) {
            const x2 = Number(this.checkSetupDetails.X2);
            const y2 = Number(this.checkSetupDetails.Y2);
            if ((!isNaN(x2) && x2 > 0) || (!isNaN(y2) && y2 > 0)) {
              if (!isNaN(x2) && x2 > 0) {
                connectorObj.sourcePoint.x = connectorObj.sourcePoint.x + x2;
                connectorObj.targetPoint.x = connectorObj.targetPoint.x + x2;
              }
              if (!isNaN(y2) && y2 > 0) {
                connectorObj.sourcePoint.y = connectorObj.sourcePoint.y + y2;
                connectorObj.targetPoint.y = connectorObj.targetPoint.y + y2;
              }
            } else {
              connectorObj.sourcePoint.y = connectorObj.sourcePoint.y + ECheckLayout.DiagramHeight;
              connectorObj.targetPoint.y = connectorObj.targetPoint.y + ECheckLayout.DiagramHeight;
            }
            (this.diagramPrint as Diagram).add(JSON.parse(JSON.stringify(connectorObj)));
          } else if (page.Id == EPrintPart.Bottom) {
            const x = this.checkLayoutForm.get('PrintOrientation').value == EPrintOrientation['2PerPage'] ? Number(this.checkSetupDetails.X2) : Number(this.checkSetupDetails.X3);
            const y = this.checkLayoutForm.get('PrintOrientation').value == EPrintOrientation['2PerPage'] ? Number(this.checkSetupDetails.Y2) : Number(this.checkSetupDetails.Y3);
            if ((!isNaN(x) && x > 0) || (!isNaN(y) && y > 0)) {
              if (!isNaN(x) && x > 0) {
                connectorObj.sourcePoint.x = connectorObj.sourcePoint.x + x;
                connectorObj.targetPoint.x = connectorObj.targetPoint.x + x;
              }
              if (!isNaN(y) && y > 0) {
                connectorObj.sourcePoint.y = connectorObj.sourcePoint.y + y;
                connectorObj.targetPoint.y = connectorObj.targetPoint.y + y;
              }
            } else {
              const offsetY = this.checkLayoutForm.get('PrintOrientation').value == EPrintOrientation['2PerPage'] ? ECheckLayout.DiagramHeight : ECheckLayout.ThirdDiagramHeightOffset;
              connectorObj.sourcePoint.y = connectorObj.sourcePoint.y + offsetY;
              connectorObj.targetPoint.y = connectorObj.targetPoint.y + offsetY;
            }
            (this.diagramPrint as Diagram).add(JSON.parse(JSON.stringify(connectorObj)));
          }
        });
      }
      if (page.Nodes.length > 0) {
        page.Nodes.forEach(node => {
          let nodeObj = JSON.parse(JSON.stringify(node));
          if (page.Id == EPrintPart.Top) {
            const x1 = Number(this.checkSetupDetails.X1);
            const y1 = Number(this.checkSetupDetails.Y1);
            if ((!isNaN(x1) && x1 > 0) || (!isNaN(y1) && y1 > 0)) {
              if (!isNaN(x1) && x1 > 0) {
                nodeObj.offsetX = nodeObj.offsetX + x1;
              }
              if (!isNaN(y1) && y1 > 0) {
                nodeObj.offsetY = nodeObj.offsetY + y1;
              }
            }
            (this.diagramPrint as Diagram).add(JSON.parse(JSON.stringify(nodeObj)));
          } else if (page.Id == EPrintPart.Middle) {
            const x2 = Number(this.checkSetupDetails.X2);
            const y2 = Number(this.checkSetupDetails.Y2);
            if ((!isNaN(x2) && x2 > 0) || (!isNaN(y2) && y2 > 0)) {
              if (!isNaN(x2) && x2 > 0) {
                nodeObj.offsetX = nodeObj.offsetX + x2;
              }
              if (!isNaN(y2) && y2 > 0) {
                nodeObj.offsetY = nodeObj.offsetY + y2;
              }
            } else {
              nodeObj.offsetY = nodeObj.offsetY + ECheckLayout.DiagramHeight;
            }
            (this.diagramPrint as Diagram).add(JSON.parse(JSON.stringify(nodeObj)));
          } else if (page.Id == EPrintPart.Bottom) {
            const x = this.checkLayoutForm.get('PrintOrientation').value == EPrintOrientation['2PerPage'] ? Number(this.checkSetupDetails.X2) : Number(this.checkSetupDetails.X3);
            const y = this.checkLayoutForm.get('PrintOrientation').value == EPrintOrientation['2PerPage'] ? Number(this.checkSetupDetails.Y2) : Number(this.checkSetupDetails.Y3);
            if ((!isNaN(x) && x > 0) || (!isNaN(y) && y > 0)) {
              if (!isNaN(x) && x > 0) {
                nodeObj.offsetX = nodeObj.offsetX + x;
              }
              if (!isNaN(y) && y > 0) {
                nodeObj.offsetY = nodeObj.offsetY + y;
              }
            } else {
              const offsetY = this.checkLayoutForm.get('PrintOrientation').value == EPrintOrientation['2PerPage'] ? ECheckLayout.DiagramHeight : ECheckLayout.ThirdDiagramHeightOffset;
              nodeObj.offsetY = nodeObj.offsetY + offsetY;
            }
            (this.diagramPrint as Diagram).add(JSON.parse(JSON.stringify(nodeObj)));
          }
        });
      }
    });
    this.printDiagram();
  }

  async printDiagram() {
    try {
      this.diagramPrint.pageSettings = { width: this.pageSettings.width, height: 1500 };
      const rawSvg = this.diagramPrint.exportDiagram({
        format: 'SVG',
        mode: 'Data',
        region: 'PageSettings'
      });
      const svgString = typeof rawSvg === 'string' ? rawSvg : rawSvg?.outerHTML ?? '';

      const htmlContent = `
          <div style="text-align: center;">
            ${svgString}
          </div>
        `;

      printJS({
        printable: htmlContent,
        type: 'raw-html',
        style: `
          @media print {
            @page {
              size: A4;
              margin: 0;
            }
      
            body {
              margin: 0;
              padding: 0;
            }
      
            .svg-container {
              display: flex;
              justify-content: center;
              align-items: center;
              width: 100%;
              height: 90vh;
              overflow: hidden;
            }
      
            .svg-container svg {
              max-width: 100%;
              max-height: 100%;
              width: auto;
              height: auto;
            }
            @font-face {
              font-family: 'MICR';
              src: url('/assets/fonts/MICR/micr.ttf') format('truetype');
              font-weight: 600;
              font-style: normal;
            }
          }
        `,
      });
    }
    catch {
      const svgElement = await this.diagramPrint.exportDiagram({
        format: 'SVG',
        mode: 'Data',
        region: 'Content',
      });

      // Convert DOM element to string
      const svgString = (svgElement as SVGSVGElement).outerHTML;
      const bounds = this.diagramPrint.getDiagramBounds();
      const viewBox = `0 0 ${bounds.width} ${bounds.height}`;

      // Inject viewBox and preserveAspectRatio
      let svgWithAttrs = svgString
        .replace(
          /<svg([^>]*)(?<!viewBox=["'][^"']*["'])/,
          `<svg viewBox="${viewBox}" preserveAspectRatio="xMidYMid meet"$1`
        )
        .replace(/width="[^"]*"/, 'width="100vw"')
        .replace(/height="[^"]*"/, 'height="auto"');

      var htmlContent = "<div id='print-section'>" +
        "<div style='text-align: center;'>" +
        svgWithAttrs
      "</div>" +
        "</div>";
      printJS({
        printable: htmlContent,
        type: 'raw-html',
        style: `
      @media print {
        html, body {
          margin: 0;
          padding: 0;
          width: 120vw;
        }
 
        h2 {
          text-align: center;
        }
 
        svg {
          width: 120vw !important;
          height: auto !important;
          display: block;
          margin: 0 auto;
        }
        @font-face {
          font-family: 'MICR';
          src: url('/assets/fonts/MICR/micr.ttf') format('truetype');
          font-weight: 600;
          font-style: normal;
        }
      }
    `,
      });
    }
  }

  public clickDiagramNode(event): void {
    if (event.element && event.element.id) {
      if (event.element.id) {
        this.checkLayoutForm.patchValue({ CheckFieldId: event.element.properties.addInfo.ControlName });
        this.changeCheckFields(event.element.properties.addInfo.ControlName, event.position, true);
      } else {
        this.changeCheckFields(null, null, false);
      }
    }
  }

  loadSaveModel() {
    let controlToSave: ICheckLayoutSetUp[] = [];
    this.checkPrintPositions.forEach(page => {
      if (page.Connectors.length > 0) {
        let connectors = JSON.parse(JSON.stringify(page.Connectors));
        connectors.forEach(conn => {
          controlToSave.push(this.modelForEachConnector(conn));
        });
      }
      if (page.Nodes.length > 0) {
        let nodes = JSON.parse(JSON.stringify(page.Nodes));
        nodes.forEach(node => {
          controlToSave.push(this.modelForEachNode(node));
        });
      }
    });
    return controlToSave;
  }

  modelForEachConnector(connector): ICheckLayoutSetUp {
    const saveModel: ICheckLayoutSetUp = {
      CheckLayoutSetupId: connector.addInfo.CheckLayoutSetupId,
      ControlName: connector.addInfo.ControlName,
      ControlCaption: connector.addInfo.ControlCaption,
      IsVisible: connector.visible,
      XPosition: connector.sourcePoint.x,
      YPosition: connector.sourcePoint.y,
      DefaultXPosition: connector.addInfo.DefaultXPosition,
      DefaultYPosition: connector.addInfo.DefaultYPosition,
      DefaultValue: connector.addInfo.DefaultValue,
      IsLine: true,
      LineSize: connector.style.strokeWidth,
      LineLength: Math.abs(connector.targetPoint.x - connector.sourcePoint.x),
      DefaultLineLength: Math.abs(connector.addInfo.DefaultYPosition - connector.addInfo.DefaultXPosition),
      FontNameId: this.fontNameList.find(f => f.Name == connector.annotations[0].style.fontFamily)?.Id,
      FontStyleId: connector.annotations[0].style.bold ? this.fontStyleList.find(f => f.Name == ECheckLayout.Bold).Id : (connector.annotations[0].style.italic ? this.fontStyleList.find(f => f.Name == ECheckLayout.Italic).Id : null),
      FontSize: connector.annotations[0].style.fontSize,
      CheckPrintPart: connector.addInfo.CheckPrintPart
    };
    return saveModel;
  }

  modelForEachNode(node): ICheckLayoutSetUp {
    const isSignature = node.addInfo.ControlName === 'imgSignature';
    const annotation = !isSignature ? node.annotations?.[0] : null;
    const saveModel: ICheckLayoutSetUp = {
      CheckLayoutSetupId: node.addInfo.CheckLayoutSetupId,
      ControlName: node.addInfo.ControlName,
      ControlCaption: node.addInfo.ControlCaption,
      IsVisible: node.visible,
      XPosition: node.offsetX,
      YPosition: node.offsetY,
      DefaultXPosition: node.addInfo.DefaultXPosition,
      DefaultYPosition: node.addInfo.DefaultYPosition,
      DefaultValue: node.addInfo.DefaultValue,
      IsLine: false,
      LineSize: null,
      LineLength: isSignature ? node.width : null,
      DefaultLineLength: null,
      FontNameId: !isSignature ? this.fontNameList.find(f => f.Name == annotation?.style?.fontFamily)?.Id : null,
      FontStyleId: !isSignature ? (annotation?.style?.bold ? this.fontStyleList.find(f => f.Name == ECheckLayout.Bold)?.Id : (annotation?.style?.italic ? this.fontStyleList.find(f => f.Name == 'Italic')?.Id : null)) : null,
      FontSize: !isSignature ? annotation?.style?.fontSize : null,
      CheckPrintPart: node.addInfo.CheckPrintPart
    };
    return saveModel;
  }

  saveDataCheckLayout() {
    this.loaderIcon = Promise.resolve(true);
    const saveModelList = this.loadSaveModel();
    this.checkWritingService.saveCheckLayouts(saveModelList).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.loaderIcon = Promise.resolve(false);
        if (response.Success) {
          this.toastr.success(EToaster.MsgSuccessSave + $localize`:@@CheckLayout:Check Layout`, EToaster.TitleSuccess, this.sharedHTMLSettings.ToasterConfig);
        } else {
          this.toastr.error(EToaster.MsgErrorSave + $localize`:@@CheckLayout:Check Layout`, EToaster.TitleError, this.sharedHTMLSettings.ToasterConfig);
        }
      },
      error: (error) => {
        this.loaderIcon = Promise.resolve(false);
        this.toastr.error(EToaster.MsgErrorSave + $localize`:@@CheckLayout:Check Layout`, EToaster.TitleError, this.sharedHTMLSettings.ToasterConfig);
      },
    });
  }

  saveCheckLayout() {
    Swal.fire({
      title: $localize`:@@Confirm:Confirm`,
      text: $localize`:@@ConfirmSaveCheckLayout:Are you sure you want to save this Check Layout?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: $localize`:@@Yes:Yes`,
      cancelButtonText: $localize`:@@No:No`
    }).then((result) => {
      if (result.isConfirmed) {
        this.saveDataCheckLayout();
      }
    });
  }

  private isSignatureControl(controlName: string): boolean {
    return controlName === 'imgSignature';
  }

  onBeforeOpen(args: any): void {
    if (!args || !args.target || !args.target.dataset || !args.target.dataset.content) {
      args.cancel = true;
    }
  }
}
